// src/pages/DashboardPage.js — "your work for the day"
// [marker: dashboard v1]
// Reminders at the top, then: money to collect (overdue invoices and membership dues — send a
// pre-written reminder with the invoice attached, remind me later, or skip), my tasks, upcoming
// events (tickets, new vs. returning buyers, the email checklist) and membership renewals.
// What each person sees is set in Settings → Dashboard.
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import EmailComposer from '../components/EmailComposer';
import { CreateTaskModal } from './TasksPage';

const money = (n) => `$${parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const money0 = (n) => `$${Math.round(parseFloat(n || 0)).toLocaleString('en-US')}`;
const fmtDay = (s, opts = { month: 'short', day: 'numeric', year: 'numeric' }) => (s ? new Date(`${String(s).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', opts) : '');
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
const TONE = {
  danger: { bg: '#fef2f2', border: '#fecaca', color: '#b91c1c' },
  warn: { bg: '#fffbeb', border: '#fde68a', color: '#92400e' },
  info: { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af' },
};
const SNOOZE = [
  { label: 'Tomorrow', days: 1 }, { label: 'In 1 week', days: 7 }, { label: 'In 2 weeks', days: 14 }, { label: 'In 1 month', days: 30 },
];

export default function DashboardPage() {
  const { user, isManagerOrAbove } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [popup, setPopup] = useState(false);
  const [queue, setQueue] = useState(null);       // { items, index }
  const [composer, setComposer] = useState(null); // { item, after }
  const [addTask, setAddTask] = useState(false);
  const canSend = isManagerOrAbove();

  const load = useCallback(() => api.get('/dashboard').then(r => { setData(r.data); setError(null); return r.data; })
    .catch(err => setError(err.response?.data?.error || 'Could not load the dashboard.')), []);

  useEffect(() => {
    load().then(d => {
      if (!d || !d.settings?.popup || !(d.reminders || []).length) return;
      let seen = null;
      try { seen = sessionStorage.getItem('njjba_day_popup'); } catch {}
      if (!seen) { setPopup(true); try { sessionStorage.setItem('njjba_day_popup', '1'); } catch {} }
    });
  }, [load]);

  const snooze = async (item, opts) => {
    try {
      const r = await api.post('/dashboard/snooze', { itemKey: item.key, ...opts });
      toast.success(opts.skip ? 'Skipped for today.' : `I’ll bring it back on ${fmtDay(r.data.until, { weekday: 'short', month: 'short', day: 'numeric' })}.`);
      load();
      return true;
    } catch (err) { toast.error(err.response?.data?.error || 'Could not save.'); return false; }
  };
  const scrollTo = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  if (error) return <div className="card" style={{ padding: 24, color: 'var(--danger)' }}>{error} <button className="btn btn-secondary btn-sm" onClick={load}>Try again</button></div>;
  if (!data) return <div className="loading-page"><div className="spinner" style={{ width: 32, height: 32 }} /></div>;

  const sections = data.settings.sections;
  const rec = data.receivables;
  const ren = data.renewals;
  // money to collect: two lists
  const duesInvoices = (rec?.invoices || []).filter(i => i.invoice_type === 'membership');
  const otherInvoices = (rec?.invoices || []).filter(i => i.invoice_type !== 'membership');
  const expired = ren?.expired || [];

  return (
    <div>
      <div className="page-header" style={{ alignItems: 'flex-end' }}>
        <div>
          <h1 className="page-title">Good {getTimeOfDay()}, {user?.firstName}.</h1>
          <p className="page-subtitle">{fmtDay(data.today, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} — here’s your work for the day.</p>
        </div>
      </div>

      {/* Reminders */}
      {data.reminders.length > 0 ? (
        <div style={{ display: 'grid', gap: 6, marginBottom: 18 }}>
          {data.reminders.map((r, i) => (
            <Reminder key={i} r={r} onGo={() => (r.anchor ? scrollTo(r.anchor) : navigate(r.link))} />
          ))}
        </div>
      ) : (
        <div className="card" style={{ padding: '14px 18px', marginBottom: 18, color: 'var(--success)', fontWeight: 600 }}>🎉 Nothing needs your attention right now.</div>
      )}

      <div className="dash-grid">
        {/* Money to collect */}
        {sections.includes('receivables') && rec && (
          <div className="dash-wide" id="collect">
            <CollectCard
              canSend={canSend}
              lists={{ invoices: otherInvoices, dues: duesInvoices, expired }}
              rec={rec} ren={ren}
              onSend={(item) => setComposer({ item })}
              onSnooze={snooze}
              onWork={(items) => setQueue({ items, index: 0 })}
            />
          </div>
        )}

        {sections.includes('tasks') && data.tasks && (
          <TasksCard t={data.tasks} onAdd={() => setAddTask(true)} onChanged={load} />
        )}

        {sections.includes('renewals') && ren && (
          <RenewalsCard ren={ren} onDues={() => scrollTo('collect')} showDuesLink={sections.includes('receivables')} />
        )}

        {sections.includes('events') && data.events && (
          <div className="dash-wide">
            <EventsCard events={data.events} today={data.today} onChanged={load} />
          </div>
        )}
      </div>

      {sections.length === 0 && (
        <div className="card" style={{ padding: 24, color: 'var(--gray-500)' }}>Your dashboard has no sections turned on. An administrator can change that under Settings → Dashboard.</div>
      )}

      {/* Pop-up: your day at a glance (once per sign-in) */}
      {popup && (
        <div className="modal-overlay" onClick={() => setPopup(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <span className="modal-title">Your day at a glance</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setPopup(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: 6, maxHeight: '60vh', overflow: 'auto' }}>
              {data.reminders.map((r, i) => <Reminder key={i} r={r} onGo={() => { setPopup(false); if (r.anchor) setTimeout(() => scrollTo(r.anchor), 50); else navigate(r.link); }} />)}
            </div>
            <div className="modal-footer">
              {canSend && rec && rec.invoices.filter(i => !i.recently_reminded).length > 0 && (
                <button className="btn btn-secondary" onClick={() => { setPopup(false); setQueue({ items: rec.invoices.filter(i => !i.recently_reminded), index: 0 }); }}>
                  Work through overdue invoices
                </button>
              )}
              <button className="btn btn-primary" onClick={() => setPopup(false)}>Let’s go</button>
            </div>
          </div>
        </div>
      )}

      {queue && (
        <WorkQueue
          queue={queue} setQueue={setQueue} canSend={canSend}
          onSend={(item) => setComposer({ item, after: () => setQueue(q => (q ? { ...q, index: q.index + 1 } : q)) })}
          onSnooze={async (item, opts) => { if (await snooze(item, opts)) setQueue(q => ({ ...q, index: q.index + 1 })); }}
          onClose={() => { setQueue(null); load(); }}
        />
      )}

      {composer && (
        <EmailComposer
          kind="invoice" id={composer.item.id}
          initialTemplate={composer.item.invoice_type === 'membership' ? 'membership_dues_reminder' : 'invoice_friendly_reminder'}
          reminder
          title={`Reminder — ${composer.item.customer}, invoice ${composer.item.invoice_number}`}
          sendLabel="Send Reminder"
          onClose={() => setComposer(null)}
          onSent={() => { const after = composer.after; setComposer(null); load(); after && after(); }}
        />
      )}

      {addTask && <CreateTaskModal onClose={() => setAddTask(false)} onSaved={() => { setAddTask(false); load(); }} />}
    </div>
  );
}

function Reminder({ r, onGo }) {
  const t = TONE[r.tone] || TONE.info;
  return (
    <button onClick={onGo} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', cursor: 'pointer',
      background: t.bg, border: `1px solid ${t.border}`, color: t.color, borderRadius: 8, padding: '9px 12px', fontSize: '0.9rem', font: 'inherit' }}>
      <span style={{ fontSize: '1.05rem' }}>{r.icon}</span>
      <span style={{ flex: 1, fontWeight: 600 }}>{r.text}</span>
      <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>Open →</span>
    </button>
  );
}

// ─── Remind me picker ─────────────────────────────────────────────────────────
function SnoozeMenu({ onPick, small = true }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState('');
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button className={`btn btn-secondary${small ? ' btn-sm' : ''}`} onClick={() => setOpen(o => !o)}>⏰ Remind me ▾</button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 41, background: 'white', border: '1px solid var(--gray-200)',
            borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 6, minWidth: 190 }}>
            {SNOOZE.map(s => (
              <div key={s.days} className="dash-menu-item" onClick={() => { setOpen(false); onPick({ days: s.days }); }}>{s.label}</div>
            ))}
            <div style={{ borderTop: '1px solid var(--gray-100)', marginTop: 4, paddingTop: 6, display: 'flex', gap: 4 }}>
              <input type="date" className="form-input" style={{ height: 30, fontSize: '0.8rem', padding: '2px 6px' }} value={date} onChange={e => setDate(e.target.value)} />
              <button className="btn btn-primary btn-sm" disabled={!date} onClick={() => { setOpen(false); onPick({ until: date }); }}>OK</button>
            </div>
          </div>
        </>
      )}
    </span>
  );
}

// ─── Money to collect ─────────────────────────────────────────────────────────
function CollectCard({ canSend, lists, rec, ren, onSend, onSnooze, onWork }) {
  const [tab, setTab] = useState(lists.invoices.length || !(lists.dues.length + lists.expired.length) ? 'invoices' : 'dues');
  const [hideReminded, setHideReminded] = useState(true);
  const rows = tab === 'invoices' ? lists.invoices : lists.dues;
  const shown = rows.filter(i => !hideReminded || !i.recently_reminded);
  const remindedHere = rows.filter(i => i.recently_reminded).length;
  const total = (l) => l.reduce((s, i) => s + i.balance_due, 0);
  return (
    <div className="card">
      <div className="card-header" style={{ flexWrap: 'wrap', gap: 8 }}>
        <span className="card-title">💵 Money to collect</span>
        <span style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>
          {plural(rec.totals.count, 'overdue invoice')} · {money(rec.totals.amount)}
          {rec.snoozed ? ` · ${rec.snoozed} set aside for later` : ''}
        </span>
      </div>
      <div className="tabs" style={{ padding: '0 16px', marginBottom: 0 }}>
        <button className={`tab-btn${tab === 'invoices' ? ' active' : ''}`} onClick={() => setTab('invoices')}>Overdue invoices <Count n={lists.invoices.length} /></button>
        <button className={`tab-btn${tab === 'dues' ? ' active' : ''}`} onClick={() => setTab('dues')}>Membership dues <Count n={lists.dues.length + lists.expired.length} /></button>
      </div>
      <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderBottom: '1px solid var(--gray-100)' }}>
        {canSend && shown.length > 0 && (
          <button className="btn btn-primary btn-sm" onClick={() => onWork(shown)}>▶ Work through {shown.length === 1 ? 'it' : `these ${shown.length}`} one by one</button>
        )}
        <span style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>{rows.length ? `${money(total(rows))} open` : ''}</span>
        {remindedHere > 0 && (
          <label style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--gray-600)', display: 'inline-flex', gap: 5, alignItems: 'center' }}>
            <input type="checkbox" checked={hideReminded} onChange={e => setHideReminded(e.target.checked)} /> Hide {remindedHere} reminded in the last week
          </label>
        )}
      </div>
      <div style={{ maxHeight: 520, overflow: 'auto' }}>
        {shown.length === 0 && !(tab === 'dues' && lists.expired.length) && (
          <div style={{ padding: 20, color: 'var(--gray-500)', textAlign: 'center' }}>{rows.length ? 'Everything here was reminded in the last week.' : 'Nothing overdue here. 🎉'}</div>
        )}
        {shown.map(i => <InvoiceRow key={i.id} i={i} canSend={canSend} onSend={onSend} onSnooze={onSnooze} />)}
        {tab === 'dues' && lists.expired.length > 0 && (
          <>
            <div style={{ padding: '10px 16px 4px', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase' }}>
              Past the renewal date — no dues invoice yet
            </div>
            {lists.expired.map(m => <ExpiredRow key={m.key} m={m} onSnooze={onSnooze} />)}
          </>
        )}
      </div>
      {ren && ren.expiredSnoozed > 0 && tab === 'dues' && <div style={{ padding: '6px 16px', fontSize: '0.78rem', color: 'var(--gray-500)' }}>{ren.expiredSnoozed} more set aside for later.</div>}
    </div>
  );
}
const Count = ({ n }) => <span style={{ background: n ? 'var(--danger-bg)' : 'var(--gray-100)', color: n ? 'var(--danger)' : 'var(--gray-500)', borderRadius: 10, padding: '0 7px', fontSize: '0.75rem', marginLeft: 4 }}>{n}</span>;

function InvoiceRow({ i, canSend, onSend, onSnooze }) {
  const custLink = i.business_id ? `/members/${i.business_id}` : i.contact_id ? `/contacts/${i.contact_id}` : null;
  return (
    <div className="dash-row" style={{ opacity: i.recently_reminded ? 0.65 : 1 }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontWeight: 600 }}>{custLink ? <Link to={custLink}>{i.customer}</Link> : i.customer}</div>
        <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>
          <Link to={`/invoices/${i.id}`}>{i.invoice_number}</Link> · {i.items || i.invoice_type} · due {fmtDay(i.due_date)}
          {i.last_reminder && <> · <span style={{ color: 'var(--info)' }}>reminded {fmtDay(i.last_reminder, { month: 'short', day: 'numeric' })}{i.reminders > 1 ? ` (${i.reminders}×)` : ''}</span></>}
          {!i.email && <> · <span style={{ color: 'var(--danger)' }}>no email on file</span></>}
        </div>
      </div>
      <div style={{ textAlign: 'right', minWidth: 110 }}>
        <div style={{ fontWeight: 700 }}>{money(i.balance_due)}</div>
        <div style={{ fontSize: '0.75rem', color: i.days_late > 60 ? 'var(--danger)' : 'var(--warning)' }}>{plural(i.days_late, 'day')} late</div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {canSend && <button className="btn btn-primary btn-sm" onClick={() => onSend(i)}>✉ Send reminder</button>}
        <SnoozeMenu onPick={(o) => onSnooze(i, o)} />
        <button className="btn btn-ghost btn-sm" onClick={() => onSnooze(i, { skip: true })} title="Hide until tomorrow">Skip</button>
      </div>
    </div>
  );
}

function ExpiredRow({ m, onSnooze }) {
  return (
    <div className="dash-row">
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontWeight: 600 }}><Link to={`/members/${m.id}`}>{m.business_name}</Link></div>
        <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>
          {m.membership_type || 'Member'} · renewal was {fmtDay(m.renewal_date)} ({plural(m.days_past, 'day')} ago)
          {m.status !== 'active' && <> · <span className={`badge badge-${m.status === 'grace_period' ? 'grace' : m.status}`} style={{ fontSize: '0.65rem' }}>{m.status.replace('_', ' ')}</span></>}
          {m.email ? '' : <> · <span style={{ color: 'var(--danger)' }}>no email on file</span></>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <Link className="btn btn-primary btn-sm" to={`/invoices/new?businessId=${m.id}&type=membership`}>+ Create dues invoice</Link>
        <SnoozeMenu onPick={(o) => onSnooze(m, o)} />
        <button className="btn btn-ghost btn-sm" onClick={() => onSnooze(m, { skip: true })}>Skip</button>
      </div>
    </div>
  );
}

// ─── One at a time ────────────────────────────────────────────────────────────
function WorkQueue({ queue, setQueue, canSend, onSend, onSnooze, onClose }) {
  const { items, index } = queue;
  const i = items[index];
  const done = index >= items.length;
  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1300 }}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <span className="modal-title">{done ? 'All done!' : `Follow-up ${index + 1} of ${items.length}`}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div style={{ height: 4, background: 'var(--gray-100)' }}><div style={{ height: 4, width: `${Math.min(100, (index / items.length) * 100)}%`, background: 'var(--success)', transition: 'width .3s' }} /></div>
        <div className="modal-body">
          {done ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <div style={{ fontSize: '2.4rem' }}>🎉</div>
              <p style={{ fontWeight: 600 }}>You went through all {items.length}.</p>
            </div>
          ) : (
            <>
              <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{i.customer}</div>
              <div style={{ color: 'var(--gray-500)', fontSize: '0.875rem', marginBottom: 12 }}>{i.email || 'No email on file — add one on the invoice or the contact.'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 12 }}>
                <Fact label="Open balance" value={money(i.balance_due)} strong />
                <Fact label="Was due" value={fmtDay(i.due_date)} />
                <Fact label="Late by" value={plural(i.days_late, 'day')} tone={i.days_late > 60 ? 'var(--danger)' : 'var(--warning)'} />
              </div>
              <div style={{ fontSize: '0.875rem', marginBottom: 6 }}><strong>Invoice:</strong> <Link to={`/invoices/${i.id}`} onClick={onClose}>{i.invoice_number}</Link> ({i.invoice_type}) — total {money(i.total_amount)}</div>
              <div style={{ fontSize: '0.875rem', marginBottom: 6 }}><strong>For:</strong> {i.items || '—'}</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                {i.last_reminder ? `Last reminder sent ${fmtDay(i.last_reminder)}${i.reminders > 1 ? ` (${i.reminders} so far)` : ''}.` : 'No reminder sent yet from the dashboard.'}
              </div>
            </>
          )}
        </div>
        <div className="modal-footer" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          {done ? <span /> : (
            <button className="btn btn-ghost btn-sm" disabled={index === 0} onClick={() => setQueue(q => ({ ...q, index: q.index - 1 }))}>← Back</button>
          )}
          {done ? <button className="btn btn-primary" onClick={onClose}>Close</button> : (
            <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button className="btn btn-ghost" onClick={() => onSnooze(i, { skip: true })}>Skip</button>
              <SnoozeMenu small={false} onPick={(o) => onSnooze(i, o)} />
              {canSend && <button className="btn btn-primary" disabled={!i.email} onClick={() => onSend(i)}>✉ Yes, send a reminder</button>}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
const Fact = ({ label, value, strong, tone }) => (
  <div style={{ background: 'var(--gray-50)', borderRadius: 8, padding: '8px 10px' }}>
    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--gray-500)', fontWeight: 700 }}>{label}</div>
    <div style={{ fontWeight: strong ? 800 : 600, fontSize: strong ? '1.1rem' : '0.95rem', color: tone }}>{value}</div>
  </div>
);

// ─── My tasks ─────────────────────────────────────────────────────────────────
function TasksCard({ t, onAdd, onChanged }) {
  const today = new Date(); const ymd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const complete = async (task) => {
    try { await api.patch(`/tasks/${task.id}`, { status: 'complete' }); toast.success('Done! ✔'); onChanged(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not update the task.'); }
  };
  const c = t.counts;
  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">✅ My tasks</span>
        <span style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-secondary btn-sm" onClick={onAdd}>+ Add</button>
          <Link className="btn btn-ghost btn-sm" to="/tasks">All tasks →</Link>
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, padding: '12px 16px 4px' }}>
        <Chip n={c.new} label="New" tone={c.new ? 'var(--info)' : undefined} />
        <Chip n={c.overdue} label="Overdue" tone={c.overdue ? 'var(--danger)' : undefined} />
        <Chip n={c.today} label="Due today" tone={c.today ? 'var(--warning)' : undefined} />
        <Chip n={c.week} label="This week" />
      </div>
      <div style={{ padding: '6px 6px 10px' }}>
        {t.list.length === 0 && <div style={{ padding: 14, color: 'var(--gray-500)', textAlign: 'center' }}>No open tasks. 🎉</div>}
        {t.list.map(task => {
          const late = task.due && task.due < ymd; const isToday = task.due === ymd;
          return (
            <div key={task.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '7px 10px', borderRadius: 6 }} className="dash-task">
              <input type="checkbox" title="Mark done" style={{ marginTop: 3 }} onChange={() => complete(task)} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: task.is_read_by_assignee ? 500 : 700 }}>
                  {!task.is_read_by_assignee && <span style={{ color: 'var(--info)', fontSize: '0.7rem', marginRight: 6 }}>NEW</span>}
                  <Link to="/tasks" style={{ color: 'inherit' }}>{task.title}</Link>
                </div>
                <div style={{ fontSize: '0.75rem', color: late ? 'var(--danger)' : isToday ? 'var(--warning)' : 'var(--gray-500)' }}>
                  {task.due ? (late ? `Overdue — was due ${fmtDay(task.due, { month: 'short', day: 'numeric' })}` : isToday ? 'Due today' : `Due ${fmtDay(task.due, { weekday: 'short', month: 'short', day: 'numeric' })}`) : 'No due date'}
                  {task.priority && task.priority !== 'normal' ? ` · ${task.priority}` : ''}
                  {task.created_by_first ? ` · from ${task.created_by_first}` : ''}
                </div>
              </div>
            </div>
          );
        })}
        {c.open > t.list.length && <div style={{ fontSize: '0.8rem', padding: '4px 10px' }}><Link to="/tasks">+ {c.open - t.list.length} more</Link></div>}
      </div>
    </div>
  );
}
const Chip = ({ n, label, tone }) => (
  <div style={{ background: 'var(--gray-50)', borderRadius: 8, padding: '8px 6px', textAlign: 'center' }}>
    <div style={{ fontSize: '1.35rem', fontWeight: 800, color: tone || 'var(--gray-700)' }}>{n}</div>
    <div style={{ fontSize: '0.7rem', color: 'var(--gray-500)', textTransform: 'uppercase', fontWeight: 700 }}>{label}</div>
  </div>
);

// ─── Renewals ─────────────────────────────────────────────────────────────────
function RenewalsCard({ ren, onDues, showDuesLink }) {
  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">🔁 Membership renewals</span>
        <Link className="btn btn-ghost btn-sm" to="/renewals">Renewals →</Link>
      </div>
      <div style={{ padding: '10px 16px 14px', display: 'grid', gap: 10 }}>
        {ren.upcoming.length === 0 && <div style={{ color: 'var(--gray-500)' }}>No renewals in the next 90 days.</div>}
        {ren.upcoming.map(u => {
          const pct = u.members ? Math.round((u.invoiced / u.members) * 100) : 0;
          return (
            <div key={u.renewal_date} style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <strong>{fmtDay(u.renewal_date, { month: 'long', day: 'numeric', year: 'numeric' })}</strong>
                <span style={{ fontWeight: 800, color: u.days_left <= 14 ? 'var(--danger)' : u.days_left <= 30 ? 'var(--warning)' : 'var(--navy)' }}>{plural(u.days_left, 'day')} left</span>
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)', margin: '4px 0 6px' }}>
                {plural(u.members, 'member')} renew{u.members === 1 ? 's' : ''}{u.dues ? ` · about ${money0(u.dues)} in dues` : ''}
              </div>
              <div style={{ height: 8, background: 'var(--gray-100)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: 8, background: 'var(--success)' }} />
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 3 }}>{u.invoiced} of {u.members} renewal invoices sent{u.members - u.invoiced > 0 ? ` — ${u.members - u.invoiced} to go` : ' ✔'}</div>
            </div>
          );
        })}
        {(ren.expired.length > 0 || ren.graceCount > 0) && (
          <div style={{ fontSize: '0.85rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px', color: '#991b1b' }}>
            {ren.expired.length > 0 && <div>{plural(ren.expired.length, 'member')} past the renewal date with no dues invoice{showDuesLink && <> — <button className="btn btn-ghost btn-sm" style={{ padding: 0 }} onClick={onDues}>see them</button></>}</div>}
            {ren.graceCount > 0 && <div>{plural(ren.graceCount, 'member')} in the grace period</div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Events ───────────────────────────────────────────────────────────────────
function EventsCard({ events, today, onChanged }) {
  const mark = async (e, s, state) => {
    try { await api.post('/dashboard/event-email', { eventId: e.id, step: s.step, state }); onChanged(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not save.'); }
  };
  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">📅 Upcoming events</span>
        <Link className="btn btn-ghost btn-sm" to="/events">All events →</Link>
      </div>
      {events.length === 0 && <div style={{ padding: 18, color: 'var(--gray-500)' }}>No events in the next four months.</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 12, padding: events.length ? 14 : 0 }}>
        {events.map(e => {
          const tk = e.tickets; const cap = e.total_capacity || 0;
          const pct = cap ? Math.min(100, Math.round((tk.sold / cap) * 100)) : 0;
          const past = e.days_to_go < 0;
          return (
            <div key={e.id} id={`event-${e.id}`} style={{ border: '1px solid var(--gray-200)', borderRadius: 10, padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <Link to={`/events/${e.id}`} style={{ fontWeight: 700, fontSize: '1rem' }}>{e.name}</Link>
                <span style={{ whiteSpace: 'nowrap', fontWeight: 800, color: past ? 'var(--gray-500)' : e.days_to_go <= 7 ? 'var(--danger)' : 'var(--navy)' }}>
                  {past ? `${plural(-e.days_to_go, 'day')} ago` : e.days_to_go === 0 ? 'Today!' : `in ${plural(e.days_to_go, 'day')}`}
                </span>
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginBottom: 10 }}>{fmtDay(e.event_date, { weekday: 'long', month: 'long', day: 'numeric' })}{e.venue ? ` · ${e.venue}` : ''}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                <Fact label="Tickets" value={cap ? `${tk.sold} / ${cap}` : tk.sold} strong />
                <Fact label="Ticket sales" value={money0(tk.revenue)} />
                <Fact label="Sponsors" value={`${e.sponsors.count}${e.sponsors.amount ? ` · ${money0(e.sponsors.amount)}` : ''}`} />
              </div>
              {cap > 0 && <div style={{ height: 6, background: 'var(--gray-100)', borderRadius: 3, margin: '8px 0 2px', overflow: 'hidden' }}><div style={{ width: `${pct}%`, height: 6, background: 'var(--navy)' }} /></div>}
              <div style={{ fontSize: '0.8rem', color: 'var(--gray-600)', marginTop: 6, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <span>🆕 {tk.new_buyers} new {tk.new_buyers === 1 ? 'buyer' : 'buyers'}</span>
                <span>🔁 {tk.returning} returning</span>
                <span>📈 {tk.last7} in the last 7 days</span>
                {tk.comp > 0 && <span>🎟 {tk.comp} comp</span>}
                {tk.pending > 0 && <span style={{ color: 'var(--warning)' }}>⏳ {tk.pending} unpaid</span>}
                {past && <span>✔ {tk.checked_in} checked in</span>}
              </div>
              <div style={{ marginTop: 12, borderTop: '1px solid var(--gray-100)', paddingTop: 8 }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                  <span>Email checklist</span><Link to="/email" style={{ textTransform: 'none', fontWeight: 600 }}>Write an email →</Link>
                </div>
                {e.emails.map(s => (
                  <div key={s.step} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: '0.84rem', opacity: s.state ? 0.6 : 1 }}>
                    <span style={{ width: 18, textAlign: 'center' }}>{s.state === 'done' ? '✅' : s.state === 'skipped' ? '➖' : s.late ? '🔴' : s.due <= addDays(today, 7) ? '🟡' : '⚪'}</span>
                    <span style={{ flex: 1, textDecoration: s.state === 'skipped' ? 'line-through' : 'none' }}>{s.label}</span>
                    <span style={{ fontSize: '0.75rem', color: s.late ? 'var(--danger)' : 'var(--gray-500)', whiteSpace: 'nowrap' }}>{fmtDay(s.due, { month: 'short', day: 'numeric' })}</span>
                    {s.state ? (
                      <button className="btn btn-ghost btn-sm" style={{ padding: '0 6px', fontSize: '0.72rem' }} onClick={() => mark(e, s, null)}>undo</button>
                    ) : (
                      <span style={{ display: 'inline-flex', gap: 2 }}>
                        <button className="btn btn-secondary btn-sm" style={{ padding: '1px 8px', fontSize: '0.72rem' }} onClick={() => mark(e, s, 'done')}>Done</button>
                        <button className="btn btn-ghost btn-sm" style={{ padding: '1px 6px', fontSize: '0.72rem' }} onClick={() => mark(e, s, 'skipped')}>Skip</button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
function addDays(ymd, n) { const d = new Date(`${ymd}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); }

function getTimeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}
